
import { useEffect, useRef, useState } from 'react'
import { supabase } from '../lib/supabase'
import { fetchParentStudents, getActiveStudentId } from '../lib/parentStudents'

const GRADE_LEVELS = ['Junior 1','Junior 2','Junior 3','Junior 4','Junior 5','Junior 6','Middle 1', 'Middle 2', 'Middle 3', 'Senior 1', 'Senior 2']

const VIDEO_WORKER_URL = (
  process.env.REACT_APP_VIDEO_WORKER_URL || ''
).replace(/\/$/, '')

const UPLOAD_PART_SIZE = 16 * 1024 * 1024

function formatBytes(bytes) {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 MB'

  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  const index = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1
  )

  const amount = bytes / (1024 ** index)

  return `${amount.toFixed(index >= 2 ? 2 : 1)} ${units[index]}`
}

function formatRemaining(seconds) {
  if (!Number.isFinite(seconds) || seconds < 0) {
    return 'Calculating...'
  }

  if (seconds < 60) {
    return `About ${Math.ceil(seconds)} sec left`
  }

  const mins = Math.ceil(seconds / 60)

  if (mins < 60) {
    return `About ${mins} min left`
  }

  const hours = Math.floor(mins / 60)
  const remainingMins = mins % 60

  return `About ${hours} hr ${remainingMins} min left`
}

function formatDate(value) {
  if (!value) return '—'

  return new Date(value).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

async function workerFetch(path, accessToken, options = {}) {
  if (!VIDEO_WORKER_URL) {
    throw new Error(
      'Missing REACT_APP_VIDEO_WORKER_URL in your frontend environment.'
    )
  }

  const response = await fetch(`${VIDEO_WORKER_URL}${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${accessToken}`,
      ...(options.headers || {}),
    },
  })

  const data = await response.json().catch(() => ({}))

  if (!response.ok) {
    throw new Error(
      data.error || `Worker request failed (${response.status})`
    )
  }

  return data
}

function xhrRequest(method, url, headers = {}, body = null, onUploadProgress = null) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()

    xhr.open(method, url, true)

    Object.entries(headers).forEach(([key, value]) => {
      xhr.setRequestHeader(key, value)
    })

    if (onUploadProgress) {
      xhr.upload.onprogress = onUploadProgress
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(xhr)
      } else {
        let message = xhr.responseText

        try {
          message = JSON.parse(xhr.responseText).error || message
        } catch (_) {}

        reject(new Error(message || `Upload failed (${xhr.status})`))
      }
    }

    xhr.onerror = () => {
      reject(new Error('Network error during upload'))
    }

    xhr.ontimeout = () => {
      reject(new Error('Upload request timed out'))
    }

    xhr.timeout = 0
    xhr.send(body)
  })
}

async function uploadVideoResumable(file, accessToken, onProgress) {
  const created = await workerFetch(
    '/api/upload/create',
    accessToken,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        fileName: file.name,
        contentType: file.type || 'video/mp4',
        size: file.size,
      }),
    }
  )

  const { key, uploadId } = created

  const totalParts = Math.ceil(file.size / UPLOAD_PART_SIZE)
  const parts = []

  const startedAt = Date.now()

  let completedBytes = 0
  let lastProgressAt = startedAt
  let lastProgressBytes = 0

  const retryDelays = [
    1000,
    2500,
    5000,
    10000,
    20000,
  ]

  try {
    for (
      let partNumber = 1;
      partNumber <= totalParts;
      partNumber += 1
    ) {
      const start = (partNumber - 1) * UPLOAD_PART_SIZE
      const end = Math.min(
        start + UPLOAD_PART_SIZE,
        file.size
      )

      const chunk = file.slice(start, end)

      let attempt = 0
      let uploadedPart = null

      while (!uploadedPart) {
        try {
          const url =
            `${VIDEO_WORKER_URL}/api/upload/part` +
            `?key=${encodeURIComponent(key)}` +
            `&uploadId=${encodeURIComponent(uploadId)}` +
            `&partNumber=${partNumber}`

          const xhr = await xhrRequest(
            'PUT',
            url,
            {
              Authorization: `Bearer ${accessToken}`,
              'Content-Type': 'application/octet-stream',
            },
            chunk,
            event => {
              const currentBytes =
                completedBytes + Math.min(chunk.size, event.loaded)

              const now = Date.now()

              const elapsed = Math.max(
                (now - lastProgressAt) / 1000,
                0.001
              )

              const speed = Math.max(
                0,
                (currentBytes - lastProgressBytes) / elapsed
              )

              const averageSpeed =
                currentBytes /
                Math.max((now - startedAt) / 1000, 0.001)

              const effectiveSpeed =
                speed > 0 ? speed : averageSpeed

              lastProgressAt = now
              lastProgressBytes = currentBytes

              onProgress({
                bytesUploaded: currentBytes,
                bytesTotal: file.size,
                speed: effectiveSpeed,
                remaining:
                  effectiveSpeed > 0
                    ? (file.size - currentBytes) / effectiveSpeed
                    : null,
                partNumber,
                totalParts,
              })
            }
          )

          uploadedPart = JSON.parse(xhr.responseText)

        } catch (error) {
          if (attempt >= retryDelays.length) {
            throw error
          }

          await new Promise(resolve => {
            setTimeout(resolve, retryDelays[attempt])
          })

          attempt += 1
        }
      }

      parts.push({
        partNumber: uploadedPart.partNumber,
        etag: uploadedPart.etag,
      })

      completedBytes = end

      const elapsed = Math.max(
        (Date.now() - startedAt) / 1000,
        0.001
      )

      const speed = completedBytes / elapsed

      onProgress({
        bytesUploaded: completedBytes,
        bytesTotal: file.size,
        speed,
        remaining:
          speed > 0
            ? (file.size - completedBytes) / speed
            : null,
        partNumber,
        totalParts,
      })
    }

    await workerFetch(
      '/api/upload/complete',
      accessToken,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          key,
          uploadId,
          parts,
        }),
      }
    )

    return key

  } catch (error) {
    try {
      await workerFetch(
        '/api/upload/abort',
        accessToken,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ key, uploadId }),
        }
      )
    } catch (_) {
      // An abandoned multipart upload can be cleaned up later.
    }

    throw error
  }
}

async function getVideoUrl(video) {
  if (video.storage_provider === 'r2') {
    const {
      data: { session },
    } = await supabase.auth.getSession()

    if (!session?.access_token) return null

    try {
      const result = await workerFetch(
        '/api/video-url',
        session.access_token,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ videoId: video.id }),
        }
      )

      return result.url || null

    } catch (error) {
      console.error('Could not create R2 video URL:', error)
      return null
    }
  }

  // Old videos stored in Supabase continue to work.
  const { data, error } = await supabase.storage
    .from('videos')
    .createSignedUrl(video.storage_path, 60 * 60)

  if (error) return null

  return data?.signedUrl || null
}

function VideoPlayer({ url, title }) {
  if (!url) {
    return (
      <div className="empty" style={{ margin: 0 }}>
        Video unavailable
      </div>
    )
  }

  return (
    <video
      src={url}
      controls
      playsInline
      preload="metadata"
      style={{
        width: '100%',
        maxHeight: 430,
        borderRadius: 12,
        background: '#000',
        display: 'block',
      }}
      aria-label={title}
    />
  )
}

export default function Videos({ role }) {
  if (role === 'teacher' || role === 'assistant') {
    return <StaffVideos />
  }

  if (role === 'parent') {
    return <ParentVideos />
  }

  return <StudentVideos />
}

function StaffVideos() {
  const [videos, setVideos] = useState([])
  const [loading, setLoading] = useState(true)

  const [showAdd, setShowAdd] = useState(false)
  const [showTracker, setShowTracker] = useState(null)

  const [title, setTitle] = useState('')
  const [note, setNote] = useState('')
  const [selectedGrades, setSelectedGrades] = useState([])
  const [file, setFile] = useState(null)

  const [saving, setSaving] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(null)

  const [signedUrls, setSignedUrls] = useState({})

  const [tracker, setTracker] = useState({
    loading: false,
    seen: [],
    unseen: [],
  })

  const fileRef = useRef(null)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchVideos()
  }, [])

  async function fetchVideos() {
    setLoading(true)

    const { data, error } = await supabase
      .from('videos')
      .select('*')
      .order('created_at', { ascending: false })

    if (!error) {
      setVideos(data || [])

      const urls = {}

      await Promise.all(
        (data || []).map(async video => {
          urls[video.id] = await getVideoUrl(video)
        })
      )

      setSignedUrls(urls)
    }

    setLoading(false)
  }

  function toggleGrade(grade) {
    setSelectedGrades(previous =>
      previous.includes(grade)
        ? previous.filter(item => item !== grade)
        : [...previous, grade]
    )
  }

  async function addVideo(event) {
    event.preventDefault()

    if (!file) {
      return alert('Please choose a video file.')
    }

    if (!title.trim()) {
      return alert('Please enter a video title.')
    }

    if (!selectedGrades.length) {
      return alert('Please select at least one grade.')
    }

    setSaving(true)
    setUploadProgress(null)

    const {
      data: { user },
    } = await supabase.auth.getUser()

    if (!user) {
      alert('Your session expired. Please sign in again.')
      setSaving(false)
      return
    }

    const {
      data: { session },
    } = await supabase.auth.getSession()

    if (!session?.access_token) {
      alert('Your session expired. Please sign in again.')
      setSaving(false)
      return
    }

    let path

    try {
      path = await uploadVideoResumable(
        file,
        session.access_token,
        setUploadProgress
      )

    } catch (uploadError) {
      alert(
        `Upload failed: ${uploadError?.message || uploadError}`
      )

      setSaving(false)
      setUploadProgress(null)
      return
    }

    const {
      data: video,
      error: insertError,
    } = await supabase
      .from('videos')
      .insert({
        title: title.trim(),
        note: note.trim() || null,
        storage_path: path,
        storage_provider: 'r2',
        file_name: file.name,
        grade_levels: selectedGrades,
        created_by: user.id,
      })
      .select()
      .single()

    if (insertError) {
      try {
        await workerFetch(
          '/api/object/delete',
          session.access_token,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ key: path }),
          }
        )
      } catch (_) {}

      alert(insertError.message)
      setSaving(false)
      return
    }

    const url = await getVideoUrl(video)

    setVideos(previous => [video, ...previous])

    setSignedUrls(previous => ({
      ...previous,
      [video.id]: url,
    }))

    setTitle('')
    setNote('')
    setSelectedGrades([])
    setFile(null)

    if (fileRef.current) {
      fileRef.current.value = ''
    }

    setShowAdd(false)
    setSaving(false)
    setUploadProgress(null)
  }

  async function openTracker(video) {
    setShowTracker(video)

    setTracker({
      loading: true,
      seen: [],
      unseen: [],
    })

    const { data: students } = await supabase
      .from('students')
      .select('id, name, student_code, grade')
      .in('grade', video.grade_levels || [])
      .order('name')

    const { data: views } = await supabase
      .from('video_views')
      .select('student_id, watched_at')
      .eq('video_id', video.id)

    const seenMap = new Map(
      (views || []).map(view => [
        view.student_id,
        view,
      ])
    )

    const seen = (students || [])
      .filter(student => seenMap.has(student.id))
      .map(student => ({
        ...student,
        watched_at: seenMap.get(student.id).watched_at,
      }))

    const unseen = (students || []).filter(
      student => !seenMap.has(student.id)
    )

    setTracker({
      loading: false,
      seen,
      unseen,
    })
  }

  async function deleteVideo(video) {
    if (!window.confirm(`Delete "${video.title}"?`)) {
      return
    }

    const {
      data: { session },
    } = await supabase.auth.getSession()

    if (video.storage_provider === 'r2') {
      if (!session?.access_token) {
        return alert('Your session expired. Please sign in again.')
      }

      try {
        await workerFetch(
          '/api/object/delete',
          session.access_token,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              key: video.storage_path,
            }),
          }
        )
      } catch (error) {
        return alert(
          `Could not delete R2 video: ${error.message}`
        )
      }

    } else {
      const { error } = await supabase.storage
        .from('videos')
        .remove([video.storage_path])

      if (error) return alert(error.message)
    }

    const { error } = await supabase
      .from('videos')
      .delete()
      .eq('id', video.id)

    if (error) {
      return alert(
        `The video file was deleted, but its database record could not be deleted: ${error.message}`
      )
    }

    setVideos(previous =>
      previous.filter(item => item.id !== video.id)
    )
  }

  if (loading) {
    return <div className="empty">Loading...</div>
  }

  return (
    <div>
      <style>{`
        .video-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 16px;
        }

        .video-card {
          background: var(--c-surface);
          border: 1px solid var(--c-border);
          border-radius: 14px;
          overflow: hidden;
          box-shadow: var(--c-shadow);
        }

        .video-card-body {
          padding: 14px;
        }

        .video-thumb {
          background: #000;
          aspect-ratio: 16/9;
          overflow: hidden;
        }

        .video-thumb video {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .grade-pills {
          display: flex;
          flex-wrap: wrap;
          gap: 7px;
        }

        .grade-pill {
          border: 1px solid var(--c-border);
          background: var(--c-surface);
          color: var(--c-text2);
          border-radius: 999px;
          padding: 7px 11px;
          font-size: 12px;
          cursor: pointer;
        }

        .grade-pill.active {
          background: var(--c-accent-l);
          color: var(--c-accent);
          border-color: var(--c-accent);
        }

        .tracker-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
        }

        .tracker-col {
          background: var(--c-surface2);
          border: 1px solid var(--c-border);
          border-radius: 12px;
          padding: 12px;
        }

        .student-line {
          padding: 9px 0;
          border-bottom: 1px solid var(--c-border);
        }

        .student-line:last-child {
          border-bottom: 0;
        }

        @media (max-width: 767px) {
          .video-grid {
            grid-template-columns: 1fr;
          }

          .tracker-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      <div className="page-header">
        <div>
          <h2 className="page-title">Videos</h2>

          <div
            style={{
              fontSize: 12,
              color: 'var(--c-text3)',
              marginTop: 4,
            }}
          >
            Share lesson videos with one or more grades.
          </div>
        </div>

        <button
          className="btn btn-primary"
          onClick={() => setShowAdd(true)}
        >
          ＋ Add Video
        </button>
      </div>

      {!videos.length ? (
        <div className="empty">No videos yet</div>
      ) : (
        <div className="video-grid">
          {videos.map(video => (
            <div className="video-card" key={video.id}>
              <div className="video-thumb">
                {signedUrls[video.id] ? (
                  <video
                    src={signedUrls[video.id]}
                    controls
                    playsInline
                    preload="metadata"
                  />
                ) : (
                  <div
                    style={{
                      height: '100%',
                      display: 'grid',
                      placeItems: 'center',
                      color: '#fff',
                    }}
                  >
                    Loading video...
                  </div>
                )}
              </div>

              <div className="video-card-body">
                <div
                  style={{
                    fontWeight: 650,
                    fontSize: 16,
                    overflowWrap: 'anywhere',
                  }}
                >
                  {video.title}
                </div>

                {video.note && (
                  <div
                    style={{
                      fontSize: 13,
                      color: 'var(--c-text2)',
                      marginTop: 7,
                      lineHeight: 1.5,
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {video.note}
                  </div>
                )}

                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 5,
                    marginTop: 10,
                  }}
                >
                  {(video.grade_levels || []).map(grade => (
                    <span
                      key={grade}
                      className="badge badge-success"
                    >
                      {grade}
                    </span>
                  ))}
                </div>

                <div
                  style={{
                    display: 'flex',
                    gap: 8,
                    marginTop: 12,
                  }}
                >
                  <button
                    className="btn btn-secondary"
                    style={{ flex: 1 }}
                    onClick={() => openTracker(video)}
                  >
                    ➜ Track Students
                  </button>

                  <button
                    className="btn btn-danger-soft btn-sm"
                    onClick={() => deleteVideo(video)}
                  >
                    Delete
                  </button>
                </div>

                <div
                  style={{
                    fontSize: 11,
                    color: 'var(--c-text3)',
                    marginTop: 9,
                  }}
                >
                  {formatDate(video.created_at)}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showAdd && (
        <div
          className="overlay"
          onClick={event => {
            if (event.target === event.currentTarget && !saving) {
              setShowAdd(false)
            }
          }}
        >
          <form className="modal" onSubmit={addVideo}>
            <div className="modal-title">Add Video</div>

            <div className="field">
              <label>Video *</label>

              <input
                ref={fileRef}
                type="file"
                accept="video/*"
                disabled={saving}
                onChange={event => {
                  setFile(event.target.files?.[0] || null)
                }}
              />

              {file && (
                <div
                  style={{
                    fontSize: 12,
                    color: 'var(--c-text3)',
                    marginTop: 6,
                  }}
                >
                  Selected: {file.name} · {formatBytes(file.size)}
                </div>
              )}
            </div>

            <div className="field">
              <label>Title *</label>

              <input
                value={title}
                onChange={event => setTitle(event.target.value)}
                placeholder="Video title"
                required
                disabled={saving}
              />
            </div>

            <div className="field">
              <label>
                Grades * (you can choose more than one)
              </label>

              <div className="grade-pills">
                {GRADE_LEVELS.map(grade => (
                  <button
                    type="button"
                    key={grade}
                    disabled={saving}
                    className={
                      `grade-pill ${
                        selectedGrades.includes(grade)
                          ? 'active'
                          : ''
                      }`
                    }
                    onClick={() => toggleGrade(grade)}
                  >
                    {selectedGrades.includes(grade) ? '✓ ' : ''}
                    {grade}
                  </button>
                ))}
              </div>
            </div>

            <div className="field">
              <label>Note (optional)</label>

              <textarea
                value={note}
                onChange={event => setNote(event.target.value)}
                rows={3}
                placeholder="Optional note for students and parents"
                disabled={saving}
              />
            </div>

            {saving && uploadProgress && (
              <div
                style={{
                  marginTop: 14,
                  padding: 13,
                  border: '1px solid var(--c-border)',
                  borderRadius: 12,
                  background: 'var(--c-surface2)',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 10,
                    flexWrap: 'wrap',
                    fontSize: 13,
                    fontWeight: 600,
                    marginBottom: 8,
                  }}
                >
                  <span>
                    Uploading video...
                    {' '}
                    Part {uploadProgress.partNumber} of {uploadProgress.totalParts}
                  </span>

                  <span>
                    {(
                      uploadProgress.bytesUploaded /
                      uploadProgress.bytesTotal *
                      100
                    ).toFixed(1)}%
                  </span>
                </div>

                <div
                  style={{
                    height: 10,
                    background: 'var(--c-border)',
                    borderRadius: 99,
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      width: `${
                        uploadProgress.bytesUploaded /
                        uploadProgress.bytesTotal *
                        100
                      }%`,
                      height: '100%',
                      background: 'var(--c-accent)',
                      borderRadius: 99,
                      transition: 'width .2s ease',
                    }}
                  />
                </div>

                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 7,
                    fontSize: 12,
                    color: 'var(--c-text2)',
                    marginTop: 9,
                  }}
                >
                  <span>
                    {formatBytes(uploadProgress.bytesUploaded)}
                    {' of '}
                    {formatBytes(uploadProgress.bytesTotal)}
                  </span>

                  <span>
                    {formatBytes(uploadProgress.speed)}/s
                  </span>

                  <span>
                    {formatRemaining(uploadProgress.remaining)}
                  </span>
                </div>
              </div>
            )}

            {saving && !uploadProgress && (
              <div
                style={{
                  marginTop: 12,
                  fontSize: 12,
                  color: 'var(--c-text3)',
                }}
              >
                Preparing upload...
              </div>
            )}

            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-secondary"
                disabled={saving}
                onClick={() => setShowAdd(false)}
              >
                Cancel
              </button>

              <button
                type="submit"
                className="btn btn-primary"
                disabled={saving}
              >
                {saving ? 'Uploading...' : 'Upload Video'}
              </button>
            </div>
          </form>
        </div>
      )}

      {showTracker && (
        <div
          className="overlay"
          onClick={event => {
            if (event.target === event.currentTarget) {
              setShowTracker(null)
            }
          }}
        >
          <div
            className="modal"
            style={{ maxWidth: 720 }}
          >
            <div className="modal-title">
              {showTracker.title} — Student Tracking
            </div>

            {tracker.loading ? (
              <div className="empty">Loading students...</div>
            ) : (
              <div className="tracker-grid">
                <div className="tracker-col">
                  <div
                    style={{
                      fontWeight: 650,
                      marginBottom: 6,
                    }}
                  >
                    ✓ Watched ({tracker.seen.length})
                  </div>

                  {tracker.seen.length ? (
                    tracker.seen.map(student => (
                      <div
                        className="student-line"
                        key={student.id}
                      >
                        <div style={{ fontWeight: 600 }}>
                          {student.name}
                        </div>

                        <div
                          style={{
                            fontSize: 11,
                            color: 'var(--c-text3)',
                          }}
                        >
                          {student.student_code}
                          {' · '}
                          {formatDate(student.watched_at)}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div
                      style={{
                        color: 'var(--c-text3)',
                        fontSize: 13,
                      }}
                    >
                      No students yet.
                    </div>
                  )}
                </div>

                <div className="tracker-col">
                  <div
                    style={{
                      fontWeight: 650,
                      marginBottom: 6,
                    }}
                  >
                    ○ Not watched ({tracker.unseen.length})
                  </div>

                  {tracker.unseen.length ? (
                    tracker.unseen.map(student => (
                      <div
                        className="student-line"
                        key={student.id}
                      >
                        <div style={{ fontWeight: 600 }}>
                          {student.name}
                        </div>

                        <div
                          style={{
                            fontSize: 11,
                            color: 'var(--c-text3)',
                          }}
                        >
                          {student.student_code}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div
                      style={{
                        color: 'var(--c-text3)',
                        fontSize: 13,
                      }}
                    >
                      Everyone has watched it.
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="modal-actions">
              <button
                className="btn btn-primary"
                onClick={() => setShowTracker(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function StudentVideos() {
  const [student, setStudent] = useState(null)
  const [videos, setVideos] = useState([])
  const [viewed, setViewed] = useState(new Set())
  const [urls, setUrls] = useState({})
  const [loading, setLoading] = useState(true)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchData()
  }, [])

  async function fetchData() {
    setLoading(true)

    const {
      data: { user },
    } = await supabase.auth.getUser()

    if (!user) {
      setLoading(false)
      return
    }

    const { data: account } = await supabase
      .from('student_accounts')
      .select('student_id, students(id,name,grade)')
      .eq('id', user.id)
      .single()

    const currentStudent = account?.students

    if (!currentStudent) {
      setLoading(false)
      return
    }

    setStudent(currentStudent)

    const { data } = await supabase
      .from('videos')
      .select('*')
      .contains('grade_levels', [currentStudent.grade])
      .order('created_at', { ascending: false })

    const list = data || []

    const { data: views } = await supabase
      .from('video_views')
      .select('video_id')
      .eq('student_id', currentStudent.id)

    setVideos(list)

    setViewed(
      new Set((views || []).map(view => view.video_id))
    )

    const map = {}

    await Promise.all(
      list.map(async video => {
        map[video.id] = await getVideoUrl(video)
      })
    )

    setUrls(map)
    setLoading(false)
  }

  async function markWatched(videoId) {
    if (!student) return

    const { error } = await supabase
      .from('video_views')
      .upsert(
        {
          video_id: videoId,
          student_id: student.id,
          watched_at: new Date().toISOString(),
        },
        {
          onConflict: 'video_id,student_id',
        }
      )

    if (error) {
      return alert(error.message)
    }

    setViewed(previous => new Set([...previous, videoId]))
  }

  if (loading) {
    return <div className="empty">Loading...</div>
  }

  return (
    <div>
      <h2
        className="page-title"
        style={{ marginBottom: 6 }}
      >
        Videos
      </h2>

      {student && (
        <div
          style={{
            fontSize: 13,
            color: 'var(--c-text3)',
            marginBottom: 18,
          }}
        >
          Videos for {student.name}
        </div>
      )}

      {!videos.length ? (
        <div className="empty">No videos yet</div>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,360px),1fr))',
            gap: 16,
          }}
        >
          {videos.map(video => (
            <div
              className="card"
              key={video.id}
              style={{ padding: 14 }}
            >
              <VideoPlayer
                url={urls[video.id]}
                title={video.title}
              />

              <div
                style={{
                  marginTop: 12,
                  fontWeight: 650,
                  fontSize: 16,
                  overflowWrap: 'anywhere',
                }}
              >
                {video.title}
              </div>

              {video.note && (
                <div
                  style={{
                    marginTop: 7,
                    color: 'var(--c-text2)',
                    fontSize: 13,
                    lineHeight: 1.5,
                    overflowWrap: 'anywhere',
                  }}
                >
                  {video.note}
                </div>
              )}

              <button
                className={
                  `btn ${
                    viewed.has(video.id)
                      ? 'btn-secondary'
                      : 'btn-primary'
                  }`
                }
                style={{
                  width: '100%',
                  marginTop: 13,
                }}
                onClick={() => markWatched(video.id)}
              >
                {viewed.has(video.id)
                  ? 'Watch Again'
                  : 'When you finish the video, press this button'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function ParentVideos() {
  const [student, setStudent] = useState(null)
  const [videos, setVideos] = useState([])
  const [urls, setUrls] = useState({})
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function fetchData() {
      setLoading(true)

      const {
        data: { user },
      } = await supabase.auth.getUser()

      if (!user) {
        setLoading(false)
        return
      }

      const { students } = await fetchParentStudents(user.id)

      const activeId = getActiveStudentId(students)

      const currentStudent = students.find(
        item => item.id === activeId
      )

      if (!currentStudent) {
        setLoading(false)
        return
      }

      setStudent(currentStudent)

      const { data } = await supabase
        .from('videos')
        .select('*')
        .contains('grade_levels', [currentStudent.grade])
        .order('created_at', { ascending: false })

      const list = data || []

      setVideos(list)

      const map = {}

      await Promise.all(
        list.map(async video => {
          map[video.id] = await getVideoUrl(video)
        })
      )

      setUrls(map)
      setLoading(false)
    }

    fetchData()

    window.addEventListener(
      'parent-student-changed',
      fetchData
    )

    return () => {
      window.removeEventListener(
        'parent-student-changed',
        fetchData
      )
    }
  }, [])

  if (loading) {
    return <div className="empty">Loading...</div>
  }

  return (
    <div>
      <h2
        className="page-title"
        style={{ marginBottom: 6 }}
      >
        Videos
      </h2>

      {student && (
        <div
          style={{
            fontSize: 13,
            color: 'var(--c-text3)',
            marginBottom: 18,
          }}
        >
          Showing videos for{' '}
          <strong style={{ color: 'var(--c-text2)' }}>
            {student.name}
          </strong>
        </div>
      )}

      {!videos.length ? (
        <div className="empty">No videos yet</div>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,360px),1fr))',
            gap: 16,
          }}
        >
          {videos.map(video => (
            <div
              className="card"
              key={video.id}
              style={{ padding: 14 }}
            >
              <VideoPlayer
                url={urls[video.id]}
                title={video.title}
              />

              <div
                style={{
                  marginTop: 12,
                  fontWeight: 650,
                  fontSize: 16,
                  overflowWrap: 'anywhere',
                }}
              >
                {video.title}
              </div>

              {video.note && (
                <div
                  style={{
                    marginTop: 7,
                    color: 'var(--c-text2)',
                    fontSize: 13,
                    lineHeight: 1.5,
                    overflowWrap: 'anywhere',
                  }}
                >
                  {video.note}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
